import ContentContainer from '@/features/shared/ContentContainer';
import { MuiContainer } from '@/features/shared/material-ui';
import { InsetTop, SpacingSizes } from '@/utils/constants';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import React, { memo, useMemo } from 'react';
import { areEqual } from 'react-window';
import { IntroContent } from './IntroContent';
import { SDLProps } from './SDL.interface';
import { SDLtable } from './SDLtable';
import { SearchBar } from './SearchBar';

const SDL = ({
  content,
  presentation,
  contentButtons,
  theme,
  labels,
}: Br.PropsWithContentButtons<SDLProps>) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { searchKey } = content;
  const { config } = content;
  const {
    anchorLabel,
    containerBackgroundColor,
    containerBottomSpacing,
    containerTopSpacing,
    jumplink,
  } = presentation;

  const { backgroundBanner, backgroundBannerVideo, mobileBackgroundBanner, cheaders, isCached } =
    content;

  const background = useMemo(
    () => (isMobile && mobileBackgroundBanner ? mobileBackgroundBanner : backgroundBanner),
    [backgroundBanner, isMobile, mobileBackgroundBanner],
  );
  const introContainerProps = useMemo(
    () => ({
      backgroundImage: background,
      backgroundVideo: backgroundBannerVideo,
      mobileBackgroundImage: background,
      bottomBackgroundColor: containerBackgroundColor,
      bottomSpacing: SpacingSizes.small,
      topBackgroundColor: containerBackgroundColor,
      topSpacing: containerTopSpacing,
      offsetTop: true,
      insetTop: InsetTop.hero,
    }),
    [background, backgroundBannerVideo, containerBackgroundColor, containerTopSpacing],
  );

  const filtersContainerProps = useMemo(
    () => ({
      bottomSpacing: SpacingSizes.small,
      mobileBottomSpacing: SpacingSizes.none,
      topBackgroundColor: containerBackgroundColor,
      bottomBackgroundColor: containerBackgroundColor,
      topSpacing: SpacingSizes.none,
    }),
    [containerBackgroundColor],
  );

  const tableContainerProps = useMemo(
    () => ({
      bottomSpacing: containerBottomSpacing,
      topBackgroundColor: containerBackgroundColor,
      bottomBackgroundColor: containerBackgroundColor,
      topSpacing: SpacingSizes.none,
    }),
    [containerBackgroundColor, containerBottomSpacing],
  );

  return (
    <section data-testid="sdl--container">
      <ContentContainer {...introContainerProps}>
        <MuiContainer>
          <IntroContent theme={theme} {...content} jumplink={jumplink} jumplinkLabel={anchorLabel}>
            {contentButtons?.content}
          </IntroContent>
        </MuiContainer>
      </ContentContainer>
      <ContentContainer {...filtersContainerProps}>
        <MuiContainer className="medium">
          <SearchBar config={config} labels={labels} theme={theme} />
        </MuiContainer>
      </ContentContainer>
      <ContentContainer {...tableContainerProps}>
        <MuiContainer className="large">
          <SDLtable
            theme={theme}
            searchKey={searchKey}
            headers={cheaders}
            labels={labels}
            config={config}
            isCached={isCached}
          />
        </MuiContainer>
      </ContentContainer>
    </section>
  );
};

export default memo(SDL, (prevProps: any, nextProps: any) =>
  areEqual(
    { ...prevProps.presentation, ...prevProps.content, ...prevProps.theme },
    { ...nextProps.presentation, ...nextProps.content, ...nextProps.theme },
  ),
);
